import { groupBy, isEmpty } from 'lodash';
import { batch } from 'react-redux';
import { createAction } from '@reduxjs/toolkit';
import { getStore, defaultPool } from '@client/store';
import { FirestoreChangeTypes } from '@sda/enums';
import type { FetchDataType, SnapshotResult } from '@sda/sdk/interfaces';
import type { Reducers } from '@sda/enums/store';
import type { UpsertItem } from '@sda/utils/store/interfaces';

interface HandlerOptions {
  readonly removeDeletedRows?: boolean;
}

export class DefaultSnapshotHandler {
  private reducerName: Reducers;

  private removeOneAction: ReturnType<typeof createAction>;

  private removeManyAction: ReturnType<typeof createAction>;

  private setStatusAction: ReturnType<typeof createAction>;

  private upsertOneAction: ReturnType<typeof createAction>;

  private upsertManyAction: ReturnType<typeof createAction>;

  private options: HandlerOptions;

  constructor(reducerName: Reducers, options: HandlerOptions = {}) {
    this.removeOneAction = createAction(`${reducerName}/removeOne`);
    this.removeManyAction = createAction(`${reducerName}/removeMany`);
    this.setStatusAction = createAction(`${reducerName}/setStatus`);
    this.upsertOneAction = createAction(`${reducerName}/upsertOne`);
    this.upsertManyAction = createAction(`${reducerName}/upsertMany`);

    this.options = options;
    this.reducerName = reducerName;
  }

  runHandler = <S>(item: SnapshotResult<FetchDataType, S>) => {
    const { removeDeletedRows } = this.options;
    const { getState } = getStore();

    const { status } = getState()[this.reducerName] as any;

    if (!status) {
      defaultPool.dispatch(this.setStatusAction(true));
    }

    batch(() => {
      if (!isEmpty(item)) {
        if (Object.prototype.hasOwnProperty.call(item, 'payload')) {
          const { payload } = item as UpsertItem<FetchDataType>;

          const groupedPayload = groupBy(payload, 'FIRESTORE_CHANGE_TYPE');

          const added = groupedPayload[FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_ADDED];
          const modified = groupedPayload[FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_MODIFIED];
          const removed = groupedPayload[FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_REMOVED];

          if (!isEmpty(added)) {
            if (added.length === 1) {
              defaultPool.dispatch(this.upsertOneAction(added[0]));
            } else {
              defaultPool.dispatch(this.upsertManyAction(added));
            }
          }

          if (!isEmpty(modified)) {
            if (modified.length === 1) {
              defaultPool.dispatch(this.upsertOneAction(modified[0]));
            } else {
              defaultPool.dispatch(this.upsertManyAction(modified));
            }
          }

          if (removeDeletedRows && !isEmpty(removed)) {
            if (removed.length === 1) {
              defaultPool.dispatch(this.removeOneAction(removed[0].id));
            } else {
              defaultPool.dispatch(this.removeManyAction(removed.map((removeItem) => removeItem.id)));
            }
          }
        } else if (Object.prototype.hasOwnProperty.call(item, 'id')) {
          const payload = item as FetchDataType;

          if (payload.FIRESTORE_CHANGE_TYPE !== FirestoreChangeTypes.FIRESTORE_CHANGE_TYPE_REMOVED) {
            defaultPool.dispatch(this.upsertOneAction(payload));
          } else if (removeDeletedRows) {
            defaultPool.dispatch(this.removeOneAction(payload.id));
          }
        }
      }
    });
  };
}
